import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtDateTime, fmtNum, isoToLocalInput, localInputToIso } from "../lib/format";
import { pdf } from "@react-pdf/renderer";
import { ArrowLeft, FileSignature, FileMinus, Send, Trash2, Printer, CheckCircle2, Plus, AlertTriangle, RefreshCw, Download, Loader2, Pencil, X, Bell, MailWarning } from "lucide-react";
import { Button } from "../components/ui/button";
import InvoicePdf, { computeTotals as computeInvoiceTotals } from "../components/InvoicePdf";
import { InvoiceReminderPdf } from "../components/InvoiceReminderPdf";
import { AddressBookPicker } from "../components/AddressBookPicker";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { COMPANY_HEADER } from "../lib/company";
import { buildQrBillSvg, svgToPngDataUrl } from "../lib/invoiceQr";
import { InvoiceCreditBanner } from "../components/credit-notes/InvoiceCreditBanner";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", className: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]" },
  ENVOYEE: { label: "Envoyée", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  PAYEE: { label: "Payée", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  PARTIELLE: { label: "Partielle", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  EN_RETARD: { label: "En retard", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  ANNULEE: { label: "Annulée", className: "bg-[#F0F0EB] text-[#A3A6A3] border-[#E5E5E0]" },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [inv, setInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "VIREMENT", paid_at: "", notes: "" });
  const [settings, setSettings] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const printableRef = useRef(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);
  // Phase 98at — édition inline de l'adresse débiteur
  const [debtorEditOpen, setDebtorEditOpen] = useState(false);
  const [debtorForm, setDebtorForm] = useState({});
  const [linkedContact, setLinkedContact] = useState(null);  // Phase 101df-v2
  const [debtorSaving, setDebtorSaving] = useState(false);

  // Phase 101bz (18/02/2026) — Édition manuelle des références BL (source_bl_reference
  // + delivery_date). Nécessaire pour les factures existantes créées avant Phase 101bt
  // qui n'ont pas ces champs remplis, et pour la remontée « facture → pas de date
  // de livraison ». Réservé aux BROUILLON.
  const [editRefsMode, setEditRefsMode] = useState(false);
  const [refsDraft, setRefsDraft] = useState({ source_bl_reference: "", delivery_date: "", custom_comment: "", issue_date: "", legacy_reference: "", legacy_date: "" });
  const [savingRefs, setSavingRefs] = useState(false);
  const startEditRefs = () => {
    setRefsDraft({
      source_bl_reference: inv.source_bl_reference || "",
      delivery_date: isoToLocalInput(inv.delivery_date),
      custom_comment: inv.custom_comment || "",
      // Phase 101gd (Feb 2026) — date d'émission rebackdateable en BROUILLON
      // (cas : regénérer aujourd'hui une facture de juin pour un client
      // asso étudiants dont la facture papier avait un QR-Bill vide).
      issue_date: inv.issue_date || "",
      legacy_reference: inv.legacy_reference || "",
      legacy_date: inv.legacy_date || "",
    });
    setEditRefsMode(true);
  };
  const saveRefs = async () => {
    setSavingRefs(true);
    try {
      const payload = {
        source_bl_reference: refsDraft.source_bl_reference || "",
        delivery_date: refsDraft.delivery_date ? localInputToIso(refsDraft.delivery_date) : null,
        custom_comment: refsDraft.custom_comment || "",
        issue_date: refsDraft.issue_date || null,
        legacy_reference: refsDraft.legacy_reference || "",
        legacy_date: refsDraft.legacy_date || null,
      };
      await api.put(`/invoices/${id}`, payload);
      toast.success("Références mises à jour");
      setEditRefsMode(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingRefs(false);
    }
  };

  // Phase 101bx (18/02/2026) — Édition inline des lignes de facture BROUILLON.
  // Cas d'usage utilisatrice : les tarifs revendeurs sont négociés au coup par
  // coup. À la génération de facture depuis un BL, les PU proviennent du BL
  // (moyenne pondérée par lot) mais l'admin doit pouvoir les corriger avant
  // envoi — quantités, PU et description peuvent être ajustés.
  const [editLinesMode, setEditLinesMode] = useState(false);
  const [draftInvLines, setDraftInvLines] = useState([]);
  const [savingLines, setSavingLines] = useState(false);
  const startEditInvoiceLines = () => {
    setDraftInvLines((inv.lines || []).map((ln) => ({
      id: ln.id,
      description: ln.description || "",
      quantity: Number(ln.quantity) || 0,
      unit: ln.unit || "pcs",
      unit_price: Number(ln.unit_price) || 0,
      vat_rate: Number(ln.vat_rate) || 2.6,
    })));
    setEditLinesMode(true);
  };
  const cancelEditInvoiceLines = () => { setEditLinesMode(false); setDraftInvLines([]); };
  const updateDraftInvLine = (idx, patch) => {
    setDraftInvLines((prev) => prev.map((ln, i) => i === idx ? { ...ln, ...patch } : ln));
  };
  const removeDraftInvLine = (idx) => {
    setDraftInvLines((prev) => prev.filter((_, i) => i !== idx));
  };
  const saveInvoiceLines = async () => {
    if (draftInvLines.length === 0) {
      toast.error("Au moins une ligne doit rester.");
      return;
    }
    for (const ln of draftInvLines) {
      if (!ln.description || !ln.description.trim()) {
        toast.error("Description obligatoire sur chaque ligne");
        return;
      }
      if (!(Number(ln.quantity) > 0)) {
        toast.error(`Quantité invalide sur « ${ln.description} »`);
        return;
      }
      if (Number(ln.unit_price) < 0) {
        toast.error(`Prix négatif sur « ${ln.description} »`);
        return;
      }
    }
    const ok = await confirm({
      title: "Enregistrer les modifications de la facture ?",
      description: "Les prix, quantités et descriptions seront mis à jour et les totaux HT / TVA / TTC recalculés automatiquement. Aucun impact sur les BL ou mouvements de stock.",
      confirmText: "Enregistrer",
      cancelText: "Annuler",
    });
    if (!ok) return;
    setSavingLines(true);
    try {
      const payload = {
        lines: draftInvLines.map((ln) => ({
          description: ln.description.trim(),
          quantity: Number(ln.quantity),
          unit: ln.unit || "pcs",
          unit_price: Number(ln.unit_price),
          vat_rate: Number(ln.vat_rate) || 0,
        })),
      };
      await api.put(`/invoices/${id}`, payload);
      toast.success("Facture mise à jour · totaux recalculés");
      setEditLinesMode(false);
      setDraftInvLines([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingLines(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [i, p, s] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get(`/payments?invoice_id=${id}`),
        api.get(`/settings`).catch(() => ({ data: null })),
      ]);
      if (cancelled) return;
      setInv(i.data);
      setPayments(p.data);
      setSettings(s.data);
    })();
    return () => { cancelled = true; };
  }, [id, refreshKey]);
  // Alias gardé pour réduire le diff dans le reste du fichier
  const load = reload;

  const qrSvg = useMemo(() => inv ? buildQrBillSvg(inv, settings) : null, [inv, settings]);

  // Phase 96 — Convertit un SVG string en dataURL PNG via canvas (pour injection PDF).
  // react-pdf ne parse pas nativement le SVG SwissQRBill (trop complexe : text, paths, etc.),
  // donc on rasterise en amont côté navigateur.
  // Phase 101cc (18/02/2026) — Bugfix Firefox / Brave : on remplace le `blob:` URL par
  // un `data:image/svg+xml;base64,…` pour éviter le canvas tainting. Firefox et Brave
  // considèrent qu'un canvas dessiné avec une image chargée depuis un `blob:` peut
  // être « contaminé » et bloquent `toDataURL()` (SecurityError). Résultat : le PDF
  // facture ne pouvait plus se générer sur ces navigateurs stricts (BL OK car sans QR).

  // Phase 93+96 — Téléchargement PDF facture (aligné visuellement sur le rendu imprimé,
  // avec QR-Bill Swiss inclus en bas — envoi WhatsApp au client).
  // Phase 101ca (18/02/2026) — Diagnostic renforcé : le toast affiche désormais
  // le vrai message d'erreur (au lieu de « Erreur génération PDF » opaque). Le PDF
  // est également généré même si le QR-Bill échoue (fallback texte visible sur page 2).
  const downloadPdf = useCallback(async () => {
    if (!inv) return;
    setDownloadingPdf(true);
    try {
      // 1. Générer PNG du QR-Bill si SVG dispo (best-effort)
      let qrPngDataUrl = null;
      if (qrSvg) {
        try {
          qrPngDataUrl = await svgToPngDataUrl(qrSvg, 1600);
        } catch (err) {
          console.warn("[PDF-INVOICE] QR-Bill PNG conversion failed, PDF will show placeholder:", err);
        }
      } else {
        console.warn("[PDF-INVOICE] Pas de QR SVG (IBAN manquant ou adresse créancier incomplète dans Paramètres).");
      }
      // 2. Générer le PDF
      let blob;
      try {
        blob = await pdf(<InvoicePdf invoice={inv} settings={settings} qrPngDataUrl={qrPngDataUrl} />).toBlob();
      } catch (renderErr) {
        console.error("[PDF-INVOICE] react-pdf render error:", renderErr);
        // Fallback : tenter sans le QR-Bill si celui-ci était en cause
        if (qrPngDataUrl) {
          console.warn("[PDF-INVOICE] Fallback : régénération PDF sans QR-Bill.");
          blob = await pdf(<InvoicePdf invoice={inv} settings={settings} qrPngDataUrl={null} />).toBlob();
        } else {
          throw renderErr;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facture-${inv.reference || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      console.error("[PDF-INVOICE] Erreur globale :", e);
      const detail = e?.message || String(e) || "Cause inconnue";
      toast.error(`Erreur PDF : ${detail.slice(0, 180)}`);
    } finally {
      setDownloadingPdf(false);
    }
  }, [inv, settings, id, qrSvg]);

  // Phase 101co (Feb 2026) — Génération PDF d'une relance.
  // Deux modes :
  //   • historyEntry omis → POST backend pour créer une nouvelle relance
  //     (backend incrémente reminder_count), puis génère le PDF.
  //   • historyEntry fourni → régénère un PDF pour une relance existante
  //     (aucun appel backend, aucune incrémentation).
  const LEVEL_LABEL = {
    RAPPEL_1: "1er rappel",
    RAPPEL_2: "2e rappel",
    MISE_EN_DEMEURE: "Mise en demeure",
  };
  const generateReminderPdf = useCallback(async (historyEntry = null) => {
    if (!inv) return;
    setReminderBusy(true);
    try {
      let targetInv = inv;
      let level, reminderDate;

      if (historyEntry) {
        level = historyEntry.level;
        reminderDate = historyEntry.generated_at;
      } else {
        // Nouvelle relance : demande au backend d'incrémenter et récupère
        // l'invoice à jour (avec last_reminder_level + reminders mis à jour).
        const { data } = await api.post(`/invoices/${id}/generate-reminder`);
        targetInv = data;
        setInv(data);
        level = data.last_reminder_level;
        reminderDate = data.last_reminder_at;
      }

      // Prépare le QR-Bill (rasterisé) — même bulletin que la facture.
      let qrPngDataUrl = null;
      if (qrSvg) {
        try {
          qrPngDataUrl = await svgToPngDataUrl(qrSvg, 1600);
        } catch (err) {
          console.warn("[PDF-REMINDER] QR-Bill PNG conversion failed:", err);
        }
      }

      let blob;
      try {
        blob = await pdf(
          <InvoiceReminderPdf
            inv={targetInv}
            settings={settings}
            qrPngDataUrl={qrPngDataUrl}
            level={level}
            reminderDate={reminderDate}
          />
        ).toBlob();
      } catch (renderErr) {
        console.error("[PDF-REMINDER] react-pdf render error:", renderErr);
        if (qrPngDataUrl) {
          blob = await pdf(
            <InvoiceReminderPdf
              inv={targetInv}
              settings={settings}
              qrPngDataUrl={null}
              level={level}
              reminderDate={reminderDate}
            />
          ).toBlob();
        } else {
          throw renderErr;
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeLevel = (level || "rappel").toLowerCase();
      a.href = url;
      a.download = `${safeLevel}-${targetInv.reference || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(historyEntry ? "PDF régénéré" : `${LEVEL_LABEL[level] || "Relance"} générée et PDF téléchargé`);
      if (!historyEntry) await load();
    } catch (e) {
      console.error("[PDF-REMINDER] Erreur globale :", e);
      const detail = e?.response?.data?.detail || e?.message || String(e);
      toast.error(`Erreur relance : ${String(detail).slice(0, 180)}`);
    } finally {
      setReminderBusy(false);
    }
  }, [inv, id, qrSvg, settings, load]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/invoices/${id}`, { status });
      toast.success(`Statut : ${STATUS_META[status]?.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 101eg (Feb 2026) — Appliquer les prix négociés du revendeur sur la facture.
  // Utile pour rattraper les factures créées AVANT le fix Phase 101du.5.
  const applyResellerPricesToInvoice = async () => {
    const ok = await confirm({
      title: "Appliquer les prix revendeur ?",
      description: "Les prix unitaires de chaque ligne seront recalculés depuis les prix négociés du revendeur (override individuel > rabais global > tier par défaut). Les totaux HT/TVA/TTC seront mis à jour. La commande liée sera aussi alignée pour cohérence.",
      confirmText: "Appliquer les prix",
      cancelText: "Ne pas modifier",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoices/${id}/apply-reseller-prices`);
      const skipped = (data.changes || []).filter(c => c.source === "skipped_manual").length;
      if (data.changed_count > 0) {
        toast.success(`${data.changed_count} ligne(s) mise(s) à jour avec les prix ${data.reseller_name}${skipped > 0 ? ` · ${skipped} prix manuel(s) conservé(s)` : ""}`);
      } else {
        toast.info(`Aucun changement nécessaire — les prix étaient déjà alignés${skipped > 0 ? ` (${skipped} prix manuel(s) préservé(s))` : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur application prix revendeur");
    }
  };

  // Phase 101eh (Feb 2026) — Purge d'une facture PAYEE de test.
  const forcePurgeInvoice = async () => {
    const ok = await confirm({
      title: `Purger la facture ${inv.reference} ?`,
      description: "⚠️ ACTION DÉFINITIVE. Les paiements liés seront voidés (traçabilité comptable conservée), la commande liée sera détachée, et la facture sera SUPPRIMÉE définitivement. À utiliser uniquement pour nettoyer des factures de test.",
      confirmText: "Purger définitivement",
      cancelText: "Annuler",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/invoices/${id}/force-purge`);
      toast.success(`Facture ${data.invoice_reference} purgée · ${data.voided_payments} paiement(s) voidé(s)`);
      // Redirection vers la liste des factures
      window.location.href = "/factures";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur purge facture");
    }
  };

  // Phase 98w — Bascule le mode TVA (autorisé uniquement en BROUILLON côté backend)
  const toggleVatMode = async () => {
    const nextMode = !inv.prices_include_vat;
    try {
      await api.put(`/invoices/${id}`, { prices_include_vat: nextMode });
      toast.success(nextMode ? "Mode : Prix TTC-inclus (particulier)" : "Mode : Prix HT + TVA (revendeur/pro)");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur bascule TVA");
    }
  };

  const addPayment = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    try {
      await api.post("/payments", {
        invoice_id: id,
        amount: amt,
        method: payForm.method,
        paid_at: payForm.paid_at || null,
        notes: payForm.notes,
      });
      toast.success("Paiement enregistré");
      setPayOpen(false);
      setPayForm({ amount: "", method: "VIREMENT", paid_at: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87ad (17/06/2026) — Marquer comme payée en 1 clic.
  // Cas typique : client web a déjà payé via PayPal avant l'émission
  // de la facture. Au lieu de remplir le formulaire "Ajouter un paiement"
  // (5 clics : ouvrir → saisir montant → choisir méthode → date → submit),
  // ce bouton crée le Payment record automatiquement au montant exact du solde
  // restant dû, et bascule la facture en PAYEE.
  const [markingPaid, setMarkingPaid] = useState(false);
  const markAsPaid = async () => {
    if (markingPaid || !inv) return;
    const due = (inv.total_ttc || 0) - (inv.paid_amount || 0);
    if (due <= 0.005) {
      toast.info("Facture déjà payée intégralement");
      return;
    }
    // Méthode par défaut : si la CMD source est PAYEE via PayPal, on
    // peut deviner (mais on garde "VIREMENT" comme défaut neutre car l'origine
    // précise du paiement n'est pas toujours stockée sur la CMD)
    const ok = await confirm({
      title: "Marquer comme payée ?",
      description: `Un paiement de ${due.toFixed(2)} CHF (méthode : VIREMENT) sera enregistré daté d'aujourd'hui. La facture passera en statut PAYEE.`,
      confirmText: "Marquer payée",
      cancelText: "Annuler",
    });
    if (!ok) return;
    setMarkingPaid(true);
    try {
      await api.post("/payments", {
        invoice_id: id,
        amount: Math.round(due * 100) / 100,
        method: "VIREMENT",
        paid_at: new Date().toISOString(),
        notes: "Marqué payée en 1 clic (paiement déjà encaissé)",
      });
      toast.success(`Facture ${inv.reference} marquée PAYEE`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setMarkingPaid(false);
    }
  };

  const deletePayment = async (pay) => {
    const ok = await confirm({
      title: "Supprimer ce paiement ?",
      description: `${pay.amount.toFixed(2)} CHF sera retiré du solde de la facture.`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/payments/${pay.id}`);
      toast.success("Paiement supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const printInvoice = () => {
    const node = printableRef.current;
    if (!node) return;
    const win = window.open("", "_blank");
    if (!win) { toast.error("Le navigateur a bloqué l'impression. Autorisez les pop-ups."); return; }
    // <base> permet aux URLs relatives (logo, …) de se résoudre vers notre domaine
    const baseHref = window.location.origin + "/";
    win.document.write(`<!DOCTYPE html><html><head><base href="${baseHref}"><title>${inv.reference}</title>
      <style>
        @font-face {
          font-family: 'Sayonara';
          src: url('fonts/sayonara-regular.ttf') format('truetype');
          font-weight: normal; font-style: normal; font-display: block;
        }
        @font-face {
          font-family: 'Sayonara';
          src: url('fonts/sayonara-bold.ttf') format('truetype');
          font-weight: bold; font-style: normal; font-display: block;
        }
        /* Phase 87bk⁵ — Layout strict standard suisse (Option B : auto page break) :
           - .page utilise flex column avec min-height 297mm (= 1 page A4)
           - .qr-bill a margin-top: auto → reste collé en bas pour les factures courtes
           - break-inside: avoid sur .qr-bill et chaque <tr> → bascule propre en page 2
             quand la liste articles déborde
           - Adresse destinataire absolue à top:45mm / left:120mm (fenêtre B5) */
        @page { size: A4 portrait; margin: 0; }
        body { font-family: Helvetica, Arial, sans-serif; font-size: 10pt; color: #1A1C1A; margin: 0; padding: 0; }
        .page {
          position: relative;
          display: flex; flex-direction: column;
          min-height: 297mm;
          box-sizing: border-box;
          padding: 20mm 20mm 15mm 20mm;
        }
        h1 { font-size: 16pt; color: #1F4E3D; margin: 0; }
        .company-sayonara { font-family: 'Sayonara', Helvetica, Arial, sans-serif; font-size: 11pt; color: #1A1C1A; letter-spacing: 0.5pt; font-weight: bold; }
        .creditor-block { max-width: 90mm; }
        .recipient-block {
          position: absolute; top: 45mm; left: 120mm; width: 80mm; min-height: 40mm;
          padding: 4mm; background: #FAFAF7; border-radius: 2mm; box-sizing: border-box;
        }
        .label { font-size: 7pt; color: #A3A6A3; text-transform: uppercase; letter-spacing: 0.5pt; margin-bottom: 1mm; }
        .name { font-weight: bold; font-size: 11pt; }
        .invoice-banner { display: flex; justify-content: space-between; align-items: baseline; margin-top: 65mm; padding-bottom: 3mm; border-bottom: 1pt solid #E5E5E0; }
        table { width: 100%; border-collapse: collapse; margin-top: 4mm; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; break-inside: avoid; }
        th { background: #F4F4F0; padding: 2mm; text-align: left; font-size: 8pt; text-transform: uppercase; color: #575A57; border-bottom: 0.5pt solid #E5E5E0; }
        td { padding: 2mm; border-bottom: 0.5pt solid #E5E5E0; }
        .right { text-align: right; font-family: monospace; }
        .totals { margin-top: 4mm; text-align: right; page-break-inside: avoid; break-inside: avoid; }
        .totals .ttc { font-size: 12pt; font-weight: bold; border-top: 1pt solid #1A1C1A; padding-top: 2mm; }
        .qr-bill {
          margin-top: auto;
          padding-top: 6mm;
          border-top: 1pt dashed #1A1C1A;
          page-break-inside: avoid; break-inside: avoid;
          page-break-before: auto; break-before: auto;
        }
        .qr-bill svg { width: 100%; max-width: 210mm; height: auto; display: block; }
        .meta { font-size: 8pt; color: #575A57; }
        @media print { .no-print { display: none; } }
      </style>
    </head><body><div class="page">${node.innerHTML}</div></body></html>`);
    win.document.close();
    // Phase 87bk⁶ — Mesure le contenu rendu et ajuste min-height à N × 297mm
    // pour que le QR-Bill (margin-top: auto) tombe en bas de la DERNIÈRE page,
    // norme suisse (page 2 fond, même si haut vide).
    setTimeout(() => {
      try {
        const pageEl = win.document.querySelector('.page');
        if (pageEl) {
          // height en mm (96 dpi : 1mm = 3.7795px)
          const heightMm = pageEl.getBoundingClientRect().height * 25.4 / 96;
          const pageCount = Math.max(1, Math.ceil(heightMm / 297));
          pageEl.style.minHeight = (pageCount * 297) + 'mm';
        }
      } catch (e) { /* ignore measurement errors, fallback to single page */ }
      win.focus();
      win.print();
    }, 500);
  };

  if (!inv) return <div className="text-[#575A57]">Chargement…</div>;
  const meta = STATUS_META[inv.status] || STATUS_META.BROUILLON;
  // Phase 101gd (Feb 2026) — Recalcul cohérent avec le PDF (inclut remise + fees).
  // Utilisé aussi pour le "reste dû" et l'affichage multi-taux TVA.
  const invTotals = computeInvoiceTotals(inv);
  const remaining = invTotals.ttc - (inv.paid_amount || 0);
  const isOverdue = inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10) && inv.status !== "PAYEE" && inv.status !== "ANNULEE" && inv.status !== "BROUILLON";

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/factures")} data-testid="back-to-invoices">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-heading text-[#1A1C1A]">{inv.reference}</h1>
            <Badge className={meta.className}>{meta.label}</Badge>
            {isOverdue && <Badge className="bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]"><AlertTriangle className="w-3 h-3 mr-1" />En retard</Badge>}
          </div>
          <div className="text-sm text-[#575A57]"><AccountLink resellerId={inv.reseller_id} distributorId={inv.distributor_id} testid="inv-detail-account">{inv.debtor_name}</AccountLink> · échéance {fmtDate(inv.due_date)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="invoice-download-pdf">
            {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Télécharger PDF
          </Button>
          <Button variant="outline" onClick={printInvoice} data-testid="invoice-print"><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
        </div>
      </div>

      {/* Workflow */}
      <InvoiceCreditBanner inv={inv} onApplied={reload} />

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#575A57] uppercase tracking-wide mr-2">Actions</span>
          {inv.status === "BROUILLON" && (
            <Button size="sm" onClick={() => updateStatus("ENVOYEE")} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="invoice-send"><Send className="w-3.5 h-3.5 mr-1.5" /> Marquer envoyée</Button>
          )}
          {inv.status !== "PAYEE" && inv.status !== "ANNULEE" && remaining > 0.01 && (
            <Button size="sm" variant="outline" onClick={() => { setPayForm({ amount: remaining.toFixed(2), method: "VIREMENT", paid_at: "", notes: "" }); setPayOpen(true); }} data-testid="invoice-pay">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Enregistrer paiement
            </Button>
          )}
          {/* Phase 87ad — Marquer comme payée en 1 clic (cas paiement déjà encaissé) */}
          {inv.status !== "PAYEE" && inv.status !== "ANNULEE" && remaining > 0.01 && (
            <Button
              size="sm"
              onClick={markAsPaid}
              disabled={markingPaid}
              className="bg-[#2D6A4F] hover:bg-[#1F4E3D] text-white"
              data-testid="invoice-mark-paid"
              title={`Crée un Payment record de ${remaining.toFixed(2)} CHF et bascule la facture en PAYEE. Idéal pour les clients web qui ont déjà payé via PayPal.`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {markingPaid ? "…" : "Marquer payée"}
            </Button>
          )}
          {inv.status !== "ANNULEE" && inv.status !== "PAYEE" && (
            <Button size="sm" variant="ghost" className="text-[#C05C47]" onClick={() => updateStatus("ANNULEE")} data-testid="invoice-cancel">Annuler la facture</Button>
          )}
          {/* Phase 101fk (Feb 2026) — Créer un avoir depuis la facture. Reprend
              débiteur + toutes les lignes. Alain choisit restock case par case. */}
          {inv.status !== "ANNULEE" && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#8B3A1F] text-[#8B3A1F] hover:bg-[#F5E6E1]"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/credit-notes/from-invoice/${id}`);
                  toast.success(`Avoir ${data.reference} créé (brouillon)`);
                  window.open(`/notes-de-credit/${data.id}`, "_self");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur création avoir");
                }
              }}
              data-testid="invoice-create-credit-note"
              title="Crée un avoir en BROUILLON pré-rempli avec toutes les lignes de cette facture"
            >
              <FileMinus className="w-3.5 h-3.5 mr-1.5" /> Créer un avoir
            </Button>
          )}
          {/* Phase 101eh (Feb 2026) — Purge d'une facture PAYEE de test.
              Bouton visible UNIQUEMENT sur les factures PAYEE pour éviter le clic
              accidentel sur une vraie facture. Voide paiements + détache CMD + supprime. */}
          {inv.status === "PAYEE" && (
            <Button
              size="sm"
              variant="ghost"
              className="text-[#C05C47] hover:bg-[#FBEDEA]"
              onClick={forcePurgeInvoice}
              data-testid="invoice-force-purge"
              title="⚠️ Purge complète : voide les paiements, détache la commande, supprime la facture. À utiliser uniquement sur des factures de test."
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Purger (test)
            </Button>
          )}
          {/* Phase 101eg (Feb 2026) — Appliquer les prix revendeur négociés sur
              une facture non payée / non annulée. Utile quand la facture a été
              créée avant le fix 101du.5 (prix négociés non appliqués).
              Fonctionne aussi hors BROUILLON tant que le paiement n'est pas fait. */}
          {inv.status !== "ANNULEE" && inv.status !== "PAYEE" && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#5A8F76] text-[#1F4E3D] hover:bg-[#EAF4EE]"
              onClick={applyResellerPricesToInvoice}
              data-testid="invoice-apply-reseller-prices"
              title="Recalcule les prix unitaires depuis les prix négociés du revendeur, puis met à jour les totaux"
            >
              <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Appliquer prix revendeur
            </Button>
          )}
          {/* Phase 98w — Bascule mode TVA (uniquement en BROUILLON) */}
          {inv.status === "BROUILLON" && !editLinesMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={startEditInvoiceLines}
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              data-testid="invoice-edit-lines-btn"
              title="Modifier les prix négociés, quantités ou descriptions des lignes de facture (uniquement en brouillon)"
            >
              <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Modifier lignes / prix
            </Button>
          )}
          {inv.status === "BROUILLON" && editLinesMode && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEditInvoiceLines}
                disabled={savingLines}
                data-testid="invoice-edit-lines-cancel"
              >
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={saveInvoiceLines}
                disabled={savingLines}
                className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                data-testid="invoice-edit-lines-save"
              >
                {savingLines ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </>
          )}
          {inv.status === "BROUILLON" && (
            <Button
              size="sm"
              variant="outline"
              onClick={toggleVatMode}
              className={inv.prices_include_vat ? "text-[#1F4E3D] border-[#1F4E3D]" : "text-[#8C6B2F] border-[#E8D3A8]"}
              data-testid="invoice-toggle-vat-mode"
              title={inv.prices_include_vat
                ? "Basculer en mode HT + TVA (revendeur/pro)"
                : "Basculer en mode TTC-inclus (particulier/traiteur/web)"}
            >
              {inv.prices_include_vat ? "TVA : Prix TTC-inclus ✓" : "TVA : Prix HT + TVA ✓"}
            </Button>
          )}
          {/* Phase 87bk — Recharger les paramètres entreprise (créditeur + QR ref).
              Utile pour les factures créées avant la correction du bug de la collection settings.
              Phase 98aj — visible aussi sur factures non-brouillon si créancier incomplet
              (cas legacy où l'IBAN/adresse/localité manquait au moment de la création). */}
          {(inv.status === "BROUILLON" || !inv.creditor_iban || !inv.creditor_city || !inv.creditor_address) && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  await api.post(`/invoices/${id}/refresh-creditor`);
                  toast.success("Paramètres entreprise rechargés. La facture affiche maintenant ton IBAN + adresse + QR-Bill.");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur rechargement");
                }
              }}
              className={
                (!inv.creditor_iban || !inv.creditor_city || !inv.creditor_address)
                  ? "text-white bg-[#C05C47] border-[#C05C47] hover:bg-[#A64B39]"
                  : "text-[#5847A6] border-[#C8C0E8]"
              }
              data-testid="invoice-refresh-creditor"
              title={
                (!inv.creditor_iban || !inv.creditor_city || !inv.creditor_address)
                  ? "⚠ Données créancier incomplètes — cliquez pour recharger IBAN, adresse et localité depuis Paramètres."
                  : "Récupère le nom/adresse/IBAN entreprise depuis tes Paramètres → Facturation suisse. Régénère aussi la référence QR-Bill."
              }
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              {(!inv.creditor_iban || !inv.creditor_city || !inv.creditor_address)
                ? "⚠ Recharger créancier (incomplet)"
                : "Recharger entreprise"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Phase 101co (Feb 2026) — Relances de paiement (norme suisse).
          Visible dès que la facture n'est plus BROUILLON et n'est pas payée/annulée.
          Séquence : RAPPEL_1 → RAPPEL_2 → MISE_EN_DEMEURE. */}
      {inv.status !== "BROUILLON" && inv.status !== "PAYEE" && inv.status !== "ANNULEE" && (
        <Card className="border-[#E5E5E0]" data-testid="invoice-reminders-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-[#8C6B2F]" />
              <span className="text-sm font-semibold text-[#1A1C1A]">Relances de paiement</span>
              <span className="text-xs text-[#575A57]">
                (norme suisse : 1er rappel → 2e rappel → mise en demeure)
              </span>
            </div>

            {/* Historique */}
            {(inv.reminders || []).length > 0 && (
              <div className="mb-3 space-y-1">
                {(inv.reminders || []).map((r, idx) => (
                  <div
                    key={`${r.level}-${r.generated_at}-${idx}`}
                    className="flex items-center gap-2 text-xs text-[#575A57]"
                    data-testid={`reminder-history-${r.level.toLowerCase()}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#1F4E3D]" />
                    <span className="font-mono">{fmtDateTime(r.generated_at)}</span>
                    <span className="font-semibold text-[#1A1C1A]">
                      {LEVEL_LABEL[r.level] || r.level}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[#5847A6]"
                      onClick={() => generateReminderPdf(r)}
                      disabled={reminderBusy}
                      data-testid={`reminder-redownload-${r.level.toLowerCase()}`}
                      title="Régénérer le PDF de cette relance (sans nouvelle incrémentation)"
                    >
                      <Download className="w-3 h-3 mr-1" /> PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Bouton pour prochaine étape */}
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const count = Number(inv.reminder_count || 0);
                if (count === 0) {
                  return (
                    <Button
                      size="sm"
                      onClick={() => generateReminderPdf()}
                      disabled={reminderBusy}
                      className="bg-[#8C6B2F] hover:bg-[#735524] text-white"
                      data-testid="reminder-generate-1"
                    >
                      {reminderBusy
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Bell className="w-3.5 h-3.5 mr-1.5" />}
                      Générer 1er rappel
                    </Button>
                  );
                }
                if (count === 1) {
                  return (
                    <Button
                      size="sm"
                      onClick={() => generateReminderPdf()}
                      disabled={reminderBusy}
                      className="bg-[#C05C47] hover:bg-[#A64B39] text-white"
                      data-testid="reminder-generate-2"
                    >
                      {reminderBusy
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Bell className="w-3.5 h-3.5 mr-1.5" />}
                      Générer 2e rappel
                    </Button>
                  );
                }
                if (count === 2) {
                  return (
                    <Button
                      size="sm"
                      onClick={() => generateReminderPdf()}
                      disabled={reminderBusy}
                      className="bg-[#7A1F1F] hover:bg-[#611818] text-white"
                      data-testid="reminder-generate-3"
                    >
                      {reminderBusy
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <MailWarning className="w-3.5 h-3.5 mr-1.5" />}
                      Générer mise en demeure
                    </Button>
                  );
                }
                return (
                  <div className="text-xs text-[#575A57] italic">
                    Toutes les relances ont été émises. Prochaine étape : poursuite / recouvrement.
                  </div>
                );
              })()}
              <div className="flex-1" />
              <div className="text-xs text-[#A3A6A3]">
                Le PDF s&apos;imprime au format enveloppe B5 (fenêtre) — envoi manuel par courrier ou email.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 98at (09/07/2026) — Alerte visible si le débiteur est PARTIEL.
          Selon la norme SPS 2.3, un QR-Bill avec un débiteur partiel (nom sans
          ville, ou sans adresse) est REJETÉ par la banque. Ce warning invite
          à compléter — un bouton ouvre un dialog d'édition inline.
          Le QR-Bill lui-même ignore désormais un débiteur partiel (case blanche
          à remplir à la main = accepté banque). */}
      {inv.status === "BROUILLON" && inv.debtor_name && (!inv.debtor_address || !inv.debtor_postal_code || !inv.debtor_city) && (
        <Card className="border-[#C05C47]/40 bg-[#FDF4F1]" data-testid="invoice-partial-debtor-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C05C47] text-white flex items-center justify-center flex-shrink-0 text-lg font-bold">!</div>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-[#C05C47] mb-1">Adresse débiteur incomplète</div>
              <div className="text-[#575A57] mb-2">
                Le client « <strong>{inv.debtor_name}</strong> » n&apos;a pas tous les champs remplis
                {!inv.debtor_address && <span> · rue manquante</span>}
                {!inv.debtor_postal_code && <span> · code postal manquant</span>}
                {!inv.debtor_city && <span> · ville manquante</span>}.
                <br />
                Sans ces champs, le QR-Bill laissera la case débiteur vide (à remplir à la main).
                Corrigez-les directement ici pour un QR-Bill pré-rempli.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDebtorEditOpen(true)}
                className="border-[#C05C47] text-[#C05C47] hover:bg-[#C05C47] hover:text-white"
                data-testid="edit-debtor-btn"
              >
                Compléter l&apos;adresse débiteur
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PRINTABLE ZONE */}
      <Card className="border-[#E5E5E0]" data-testid="invoice-printable">
        <CardContent className="p-6">
          <div ref={printableRef} style={{ position: "relative", minHeight: "260mm" }}>
            {/* Phase 87bk⁴ — Layout strict standard suisse (B5 window envelope) :
                - Créditeur compact 2 lignes max en haut à GAUCHE
                - Destinataire absolute-positioned top:30mm left:115mm (fenêtre B5)
                - Bandeau FACTURE/réf/dates avec marge top pour laisser passer le destinataire
                - QR-Bill 105mm fixé en bas (CSS print) */}
            <div className="creditor-block" style={{ maxWidth: "90mm" }}>
              <div style={{ display: "flex", gap: "5mm", alignItems: "flex-start" }}>
                <img
                  src="/logo-maisonthai-pdf.png"
                  alt="Maison Thaï Conthey"
                  style={{ width: "18mm", height: "auto", flexShrink: 0 }}
                />
                <div>
                  {/* Phase 87bk⁷ — Adresse créditeur intelligente :
                      - whiteSpace: pre-line → respecte les retours à la ligne du textarea
                      - Si l'adresse contient déjà "CH-" ou le NPA → on n'ajoute pas la ligne séparée (évite duplication)
                      - Le téléphone dans le textarea est respecté ; sinon, footer "Tél : …" */}
                  <div className="company-sayonara" style={{ fontFamily: "Sayonara, Helvetica, sans-serif", fontSize: "11pt", fontWeight: "bold", letterSpacing: "0.5pt" }}>{COMPANY_HEADER}</div>
                  {inv.creditor_address && (
                    <div className="meta" style={{ fontSize: "8pt", color: "#575A57", marginTop: "1mm", whiteSpace: "pre-line" }}>
                      {inv.creditor_address}
                    </div>
                  )}
                  {(() => {
                    const addr = inv.creditor_address || "";
                    const hasCity = /ch-|\b\d{4}\b/i.test(addr) || (inv.creditor_postal_code && addr.includes(inv.creditor_postal_code));
                    if (hasCity) return null;
                    if (!(inv.creditor_postal_code || inv.creditor_city)) return null;
                    return (
                      <div className="meta" style={{ fontSize: "8pt", color: "#575A57" }}>
                        {(inv.creditor_country === "CH" || inv.creditor_country === "Suisse") ? "CH-" : ""}
                        {inv.creditor_postal_code} {inv.creditor_city}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Destinataire — positionné pour fenêtre B5 (top 45mm / left 120mm, norme suisse) */}
            <div
              className="recipient-block"
              style={{
                position: "absolute",
                top: "45mm",
                left: "120mm",
                width: "80mm",
                minHeight: "40mm",
                padding: "4mm",
                background: "#FAFAF7",
                borderRadius: "2mm",
                boxSizing: "border-box",
              }}
            >
              <div className="label" style={{ fontSize: "7pt", color: "#A3A6A3", textTransform: "uppercase", letterSpacing: "0.5pt", marginBottom: "2mm" }}>Facturé à</div>
              <div className="name" style={{ fontWeight: "bold", fontSize: "11pt" }}>{inv.debtor_name}</div>
              {inv.debtor_contact_name && <div className="meta">{inv.debtor_contact_name}</div>}
              {inv.debtor_address && <div className="meta">{inv.debtor_address}</div>}
              {(inv.debtor_postal_code || inv.debtor_city) && (
                <div className="meta">
                  {(inv.debtor_country === "CH" || inv.debtor_country === "Suisse") ? "CH-" : ""}
                  {inv.debtor_postal_code} {inv.debtor_city}
                </div>
              )}
            </div>

            {/* Bandeau FACTURE / réf / dates — marge-top pour laisser passer le destinataire absolute */}
            <div className="invoice-banner" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: "50mm", marginBottom: "6mm", paddingBottom: "3mm", borderBottom: "1pt solid #E5E5E0" }}>
              <h1 style={{ fontSize: "16pt", color: "#1F4E3D", margin: 0 }}>FACTURE</h1>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "monospace", fontSize: "11pt" }}>{inv.reference}</div>
                <div className="meta">Émise le {fmtDate(inv.issue_date)}</div>
                <div className="meta">Échéance : {fmtDate(inv.due_date)}</div>
              </div>
            </div>

            {/* Phase 101bz (18/02/2026) — Bloc « Références BL » éditable pour les factures BROUILLON.
                Cas d'usage : factures existantes (avant Phase 101bt) n'ont pas de delivery_date en DB.
                L'utilisatrice peut compléter/corriger manuellement le n° BL et la date de livraison. */}
            {(inv.source_bl_reference || inv.delivery_date || inv.custom_comment || inv.status === "BROUILLON") && (
              <div className="print-hide" style={{ marginBottom: "4mm", padding: "3mm 4mm", background: "#F4F4F0", borderLeft: "2pt solid #1F4E3D", fontSize: "10pt" }} data-testid="invoice-bl-refs">
                {inv.status === "BROUILLON" && !editRefsMode && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: "3mm" }}>
                    <div style={{ flex: 1 }}>
                      <div>
                        {inv.source_bl_reference ? (<span><strong>BL :</strong> {inv.source_bl_reference}</span>) : (<span style={{ color: "#A3A6A3" }}>Aucun BL référencé</span>)}
                        {" · "}
                        {inv.delivery_date ? (<span><strong>Livraison :</strong> {fmtDate(inv.delivery_date)}</span>) : (<span style={{ color: "#C05C47" }}>Date de livraison manquante</span>)}
                      </div>
                      {inv.custom_comment && (
                        <div style={{ marginTop: "1.5mm", fontStyle: "italic", color: "#575A57" }} data-testid="invoice-custom-comment-display">
                          <strong>Commentaire :</strong> {inv.custom_comment}
                        </div>
                      )}
                      {inv.legacy_reference && (
                        <div style={{ marginTop: "1.5mm", color: "#8C6B2F" }} data-testid="invoice-legacy-display">
                          <strong>Remplace la facture papier :</strong> {inv.legacy_reference}
                          {inv.legacy_date && <> du {fmtDate(inv.legacy_date)}</>}
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={startEditRefs} className="h-6 text-xs" data-testid="invoice-refs-edit-btn">
                      <Pencil className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                  </div>
                )}
                {inv.status === "BROUILLON" && editRefsMode && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "3mm" }}>
                    <div style={{ display: "flex", gap: "3mm", alignItems: "end", flexWrap: "wrap" }}>
                      <div>
                        <Label className="text-xs">N° BL</Label>
                        <Input value={refsDraft.source_bl_reference} onChange={(e) => setRefsDraft({ ...refsDraft, source_bl_reference: e.target.value })} className="h-8 w-40 text-xs" data-testid="invoice-refs-bl-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Date de livraison</Label>
                        <Input type="datetime-local" value={refsDraft.delivery_date} onChange={(e) => setRefsDraft({ ...refsDraft, delivery_date: e.target.value })} className="h-8 w-48 text-xs" data-testid="invoice-refs-date-input" />
                      </div>
                      <Button size="sm" onClick={saveRefs} disabled={savingRefs} className="h-8 bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="invoice-refs-save">
                        {savingRefs ? "…" : "OK"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditRefsMode(false)} className="h-8" data-testid="invoice-refs-cancel">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs">Commentaire libre (optionnel — apparaîtra dans le bloc RÉFÉRENCES du PDF)</Label>
                      <Input
                        value={refsDraft.custom_comment}
                        onChange={(e) => setRefsDraft({ ...refsDraft, custom_comment: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="Ex : Livraison partielle · Retard justifié · Prix négocié événement 20/06…"
                        maxLength={280}
                        data-testid="invoice-refs-comment-input"
                      />
                    </div>
                    {/* Phase 101gd (Feb 2026) — Backdate + trace de la facture papier remplacée.
                        Cas d'usage : facture regénérée aujourd'hui pour un événement de juin,
                        ou import papier historique (Chermignon, Oseo). */}
                    <div style={{ display: "flex", gap: "3mm", alignItems: "end", flexWrap: "wrap", borderTop: "1pt dashed #C5DBCF", paddingTop: "3mm" }}>
                      <div>
                        <Label className="text-xs">Date d'émission (fiscale)</Label>
                        <Input
                          type="date"
                          value={refsDraft.issue_date}
                          onChange={(e) => setRefsDraft({ ...refsDraft, issue_date: e.target.value })}
                          className="h-8 w-44 text-xs"
                          data-testid="invoice-refs-issue-date-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">N° facture papier remplacée</Label>
                        <Input
                          value={refsDraft.legacy_reference}
                          onChange={(e) => setRefsDraft({ ...refsDraft, legacy_reference: e.target.value })}
                          className="h-8 w-48 text-xs"
                          placeholder="ex: FAC-2026-042"
                          data-testid="invoice-refs-legacy-ref-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Date facture papier</Label>
                        <Input
                          type="date"
                          value={refsDraft.legacy_date}
                          onChange={(e) => setRefsDraft({ ...refsDraft, legacy_date: e.target.value })}
                          className="h-8 w-44 text-xs"
                          data-testid="invoice-refs-legacy-date-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {inv.status !== "BROUILLON" && (
                  <div>
                    <div>
                      {inv.source_bl_reference && (<span><strong>BL :</strong> {inv.source_bl_reference}</span>)}
                      {inv.source_bl_reference && inv.delivery_date && " · "}
                      {inv.delivery_date && (<span><strong>Livraison :</strong> {fmtDate(inv.delivery_date)}</span>)}
                    </div>
                    {inv.custom_comment && (
                      <div style={{ marginTop: "1.5mm", fontStyle: "italic", color: "#575A57" }}>
                        <strong>Commentaire :</strong> {inv.custom_comment}
                      </div>
                    )}
                    {inv.legacy_reference && (
                      <div style={{ marginTop: "1.5mm", color: "#8C6B2F" }}>
                        <strong>Remplace la facture papier :</strong> {inv.legacy_reference}
                        {inv.legacy_date && <> du {fmtDate(inv.legacy_date)}</>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Phase 87bk — Gentilé (salutation polie) avant les lignes */}
            {(inv.salutation || "").trim() && (
              <div className="meta" style={{ marginBottom: "4mm" }}>{inv.salutation}</div>
            )}

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4mm" }}>
              <thead>
                <tr style={{ background: "#F4F4F0" }}>
                  <th style={{ padding: "2mm", textAlign: "left", fontSize: "8pt", color: "#575A57" }}>Description</th>
                  <th style={{ padding: "2mm", textAlign: "right", fontSize: "8pt", color: "#575A57" }}>Qté</th>
                  <th style={{ padding: "2mm", textAlign: "right", fontSize: "8pt", color: "#575A57" }}>
                    {inv.prices_include_vat ? "PU TTC" : "PU HT"} CHF
                  </th>
                  <th style={{ padding: "2mm", textAlign: "right", fontSize: "8pt", color: "#575A57" }}>TVA %</th>
                  <th style={{ padding: "2mm", textAlign: "right", fontSize: "8pt", color: "#575A57" }}>
                    {inv.prices_include_vat ? "Total TTC" : "Total HT"}
                  </th>
                  {editLinesMode && (
                    <th style={{ padding: "2mm", textAlign: "center", fontSize: "8pt", color: "#575A57", width: "12mm" }}>—</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {editLinesMode ? draftInvLines.map((ln, idx) => (
                  <tr key={ln.id || idx} style={{ borderBottom: "0.5pt solid #E5E5E0" }} data-testid={`invoice-edit-line-${idx}`}>
                    <td style={{ padding: "1mm" }}>
                      <Input
                        value={ln.description}
                        onChange={(e) => updateDraftInvLine(idx, { description: e.target.value })}
                        className="h-7 text-xs"
                        data-testid={`invoice-edit-line-desc-${idx}`}
                      />
                    </td>
                    <td style={{ padding: "1mm", width: "18mm" }}>
                      <Input
                        type="number"
                        step="0.01"
                        value={ln.quantity}
                        onChange={(e) => updateDraftInvLine(idx, { quantity: e.target.value })}
                        className="h-7 text-xs text-right"
                        data-testid={`invoice-edit-line-qty-${idx}`}
                      />
                    </td>
                    <td style={{ padding: "1mm", width: "22mm" }}>
                      <Input
                        type="number"
                        step="0.01"
                        value={ln.unit_price}
                        onChange={(e) => updateDraftInvLine(idx, { unit_price: e.target.value })}
                        className="h-7 text-xs text-right font-mono"
                        data-testid={`invoice-edit-line-price-${idx}`}
                      />
                    </td>
                    <td style={{ padding: "1mm", width: "16mm" }}>
                      <Input
                        type="number"
                        step="0.1"
                        value={ln.vat_rate}
                        onChange={(e) => updateDraftInvLine(idx, { vat_rate: e.target.value })}
                        className="h-7 text-xs text-right"
                        data-testid={`invoice-edit-line-vat-${idx}`}
                      />
                    </td>
                    <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", fontSize: "9pt", color: "#575A57" }}>
                      {(Number(ln.quantity || 0) * Number(ln.unit_price || 0)).toFixed(2)}
                    </td>
                    <td style={{ padding: "1mm", textAlign: "center" }}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDraftInvLine(idx)}
                        className="h-7 w-7 text-[#C05C47]"
                        data-testid={`invoice-edit-line-remove-${idx}`}
                        title="Retirer cette ligne"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                )) : (inv.lines || []).map((ln, idx) => {
                  // Phase 101ea.7 — Lignes offertes : « Offert » au lieu de 0.00
                  const isOffered = Number(ln.unit_price || 0) === 0 && (ln.description || "").trim();
                  return (
                    <tr key={ln.id || idx} style={{ borderBottom: "0.5pt solid #E5E5E0" }}>
                      <td style={{ padding: "2mm" }}>{ln.description}</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace" }}>{ln.quantity} {ln.unit}</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", color: isOffered ? "#8C6B2F" : undefined }}>
                        {isOffered ? "Offert" : Number(ln.unit_price).toFixed(2)}
                      </td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", fontSize: "9pt" }}>{ln.vat_rate}%</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", color: isOffered ? "#8C6B2F" : undefined, fontWeight: isOffered ? "bold" : undefined }}>
                        {isOffered ? "Offert" : (ln.quantity * ln.unit_price).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
                {/* Phase 101ea.6 — Frais additionnels devis traiteur → facture (livraison, service, minimum garanti…)
                    Phase 101ea.7 — Frais offerts (amount=0) : afficher « Offert » */}
                {!editLinesMode && (inv.additional_fees || []).filter((f) => {
                  const hasDesc = (f.description || "").trim().length > 0;
                  const hasAmount = Number(f.amount || 0) !== 0;
                  return hasDesc || hasAmount;
                }).map((f, idx) => {
                  const amount = Number(f.amount || 0);
                  const isOffered = amount === 0;
                  return (
                    <tr key={f.id || `fee-${idx}`} style={{ borderBottom: "0.5pt solid #E5E5E0", background: "#FBF4E9" }}>
                      <td style={{ padding: "2mm", fontStyle: "italic", color: "#8C6B2F" }}>{f.description || "Frais additionnel"}</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", color: "#8C6B2F" }}>—</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", color: "#8C6B2F" }}>{isOffered ? "Offert" : "—"}</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", fontSize: "9pt", color: "#8C6B2F" }}>{isOffered ? "" : `${Number(f.vat_rate || 0)}%`}</td>
                      <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace", fontWeight: "bold", color: "#8C6B2F" }}>{isOffered ? "Offert" : amount.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {editLinesMode && (
              <div className="text-xs text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] rounded px-3 py-2 mb-2" data-testid="invoice-edit-lines-banner">
                Mode édition — modifiez les prix négociés / quantités / descriptions. Cliquez « Enregistrer » en haut pour valider et recalculer les totaux.
              </div>
            )}

            {/* Phase 87bk — Conditions de paiement au-dessus des totaux (style traditionnel) */}
            {inv.payment_terms && (
              <div style={{ marginTop: "4mm", fontSize: "9pt", color: "#1A1C1A" }}>
                <strong>Conditions de paiement :</strong> {inv.payment_terms}
              </div>
            )}

            <div style={{ marginTop: "4mm", textAlign: "right", fontSize: "10pt" }}>
              {inv.prices_include_vat ? (
                // Phase 98w — Mode TTC-inclus (privés, WEB B2C, traiteur particulier).
                // Le total affiché est directement le TTC. La TVA est mentionnée
                // légalement en dessous (obligation suisse art. 26 LTVA).
                <>
                  <div className="ttc" style={{ fontSize: "12pt", fontWeight: "bold", borderTop: "1pt solid #1A1C1A", paddingTop: "2mm" }}>
                    Total TTC : <span style={{ fontFamily: "monospace", marginLeft: "10mm" }}>{invTotals.ttc.toFixed(2)} CHF</span>
                  </div>
                  {(() => {
                    // Phase 101gd (Feb 2026) — Détail multi-taux TVA sur factures traiteur
                    // Ex: menu à 2.6% + frais/matériel à 8.1% → afficher les 2 taux.
                    const vatByRate = {};
                    Object.entries(invTotals.vatGroups || {}).forEach(([r, base]) => {
                      const rate = Number(r) || 0;
                      if (rate === 0) return;
                      const amt = base - base / (1 + rate / 100);
                      if (amt > 0.005) vatByRate[rate] = (vatByRate[rate] || 0) + amt;
                    });
                    const rates = Object.keys(vatByRate).map(Number).sort();
                    if (rates.length === 0) return null;
                    if (rates.length === 1) {
                      const r = rates[0];
                      return (
                        <div style={{ fontSize: "8pt", color: "#575A57", marginTop: "1mm" }}>
                          dont TVA {r} % : <span style={{ fontFamily: "monospace" }}>{vatByRate[r].toFixed(2)} CHF</span>
                        </div>
                      );
                    }
                    return (
                      <div style={{ fontSize: "8pt", color: "#575A57", marginTop: "1mm" }}>
                        dont TVA <span style={{ fontFamily: "monospace" }}>{invTotals.vat.toFixed(2)} CHF</span>
                        {rates.map((r) => (
                          <span key={r}> · {r} % : <span style={{ fontFamily: "monospace" }}>{vatByRate[r].toFixed(2)}</span></span>
                        ))}
                      </div>
                    );
                  })()}
                </>
              ) : (
                // Mode HT + TVA (revendeurs / pros).
                <>
                  <div>Total HT : <span style={{ fontFamily: "monospace", marginLeft: "10mm" }}>{invTotals.ht.toFixed(2)} CHF</span></div>
                  <div>TVA : <span style={{ fontFamily: "monospace", marginLeft: "10mm" }}>{invTotals.vat.toFixed(2)} CHF</span></div>
                  {/* Phase 98aj — Ligne Arrondi (Rappenrundung suisse) si écart != 0 */}
                  {Math.abs(invTotals.roundingAdjustment || 0) >= 0.005 && (
                    <div style={{ fontSize: "9pt", color: "#575A57" }}>
                      Arrondi 5 ct : <span style={{ fontFamily: "monospace", marginLeft: "10mm" }}>
                        {(invTotals.roundingAdjustment > 0 ? "+" : "") + Number(invTotals.roundingAdjustment).toFixed(2)} CHF
                      </span>
                    </div>
                  )}
                  <div className="ttc" style={{ fontSize: "12pt", fontWeight: "bold", borderTop: "1pt solid #1A1C1A", paddingTop: "2mm", marginTop: "2mm" }}>
                    Total TTC : <span style={{ fontFamily: "monospace", marginLeft: "10mm" }}>{invTotals.ttc.toFixed(2)} CHF</span>
                  </div>
                </>
              )}
              {(inv.paid_amount || 0) > 0 && (
                <div style={{ fontSize: "10pt", color: "#1F4E3D", marginTop: "2mm" }}>
                  Payé : <span style={{ fontFamily: "monospace" }}>{(inv.paid_amount || 0).toFixed(2)} CHF</span> · Reste dû : <span style={{ fontFamily: "monospace", fontWeight: "bold" }}>{remaining.toFixed(2)} CHF</span>
                </div>
              )}
            </div>

            {inv.notes && <div className="meta" style={{ marginTop: "2mm", fontSize: "8pt" }}>{inv.notes}</div>}
            {(() => {
              // Phase 87bk⁷ — Footer N° TVA + Tél, sans duplication si tél déjà dans creditor_address
              const addr = inv.creditor_address || "";
              const addrHasPhone = /tél|tel\.|\+\s?\d{2}/i.test(addr);
              const showPhone = !!inv.creditor_phone && !addrHasPhone;
              if (!inv.creditor_vat_number && !showPhone) return null;
              return (
                <div className="meta" style={{ marginTop: "2mm", fontSize: "8pt", color: "#A3A6A3", display: "flex", gap: "6mm", flexWrap: "wrap" }}>
                  {inv.creditor_vat_number && <span>N° TVA : {inv.creditor_vat_number}</span>}
                  {showPhone && <span>Tél : {inv.creditor_phone}</span>}
                </div>
              );
            })()}

            {qrSvg ? (
              <div className="qr-bill" dangerouslySetInnerHTML={{ __html: qrSvg }} />
            ) : (
              <div className="qr-bill" style={{ padding: "6mm", background: "#FCEEEA", border: "1pt solid #E8C1B4", borderRadius: "2mm", color: "#C05C47", fontSize: "9pt" }}>
                <strong>QR-Bill non généré</strong> — Renseignez l&apos;IBAN dans Paramètres → Facturation suisse pour activer le QR-Bill.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Paiements */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
            <span className="text-sm font-medium text-[#1A1C1A]">Paiements ({payments.length})</span>
            <span className="font-mono text-sm">{(inv.paid_amount || 0).toFixed(2)} / {(inv.total_ttc || 0).toFixed(2)} CHF</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Méthode</TableHead>
                <TableHead className="text-right">Montant CHF</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#A3A6A3]">Aucun paiement enregistré</TableCell></TableRow>
              ) : payments.map((p) => (
                <TableRow key={p.id} data-testid={`pay-row-${p.id}`}>
                  <TableCell className="text-xs">{fmtDateTime(p.paid_at)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{p.method}</Badge></TableCell>
                  <TableCell className="text-right font-mono font-medium">{p.amount.toFixed(2)}</TableCell>
                  <TableCell className="text-xs">{p.notes || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => deletePayment(p)} data-testid={`del-pay-${p.id}`}><Trash2 className="w-4 h-4 text-[#C05C47]" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
            <DialogDescription>Solde restant : <span className="font-mono">{remaining.toFixed(2)} CHF</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Montant CHF *</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Méthode</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VIREMENT">Virement</SelectItem>
                    <SelectItem value="CARTE">Carte</SelectItem>
                    <SelectItem value="ESPECES">Espèces</SelectItem>
                    <SelectItem value="AUTRE">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Date</Label>
                <Input type="datetime-local" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="pay-date" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes</Label>
              <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder="Optionnel" data-testid="pay-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annuler</Button>
            <Button onClick={addPayment} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pay-confirm"><CheckCircle2 className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 98at (09/07/2026) — Dialog édition inline adresse débiteur */}
      <Dialog
        open={debtorEditOpen}
        onOpenChange={(o) => {
          setDebtorEditOpen(o);
          if (o) {
            setDebtorForm({
              debtor_name: inv?.debtor_name || "",
              debtor_contact_name: inv?.debtor_contact_name || "",
              debtor_address: inv?.debtor_address || "",
              debtor_postal_code: inv?.debtor_postal_code || "",
              debtor_city: inv?.debtor_city || "",
              debtor_country: inv?.debtor_country || "CH",
            });
            setLinkedContact(null);  // Phase 101df-v2 — reset chip à chaque ouverture
          }
        }}
      >
        <DialogContent className="max-w-lg" data-testid="debtor-edit-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>Adresse débiteur</DialogTitle>
                <DialogDescription>
                  Renseignez tous les champs pour que le QR-Bill inclue le débiteur.
                  Sinon la case sera laissée blanche (à remplir à la main).
                </DialogDescription>
              </div>
              {/* Phase 101df — Import depuis carnet d'adresses
                  Phase 101df-v2 — Chip contact lié + mini-carte au survol */}
              <AddressBookPicker
                testid="invoice-address-book-picker"
                triggerLabel="Depuis le carnet"
                preferredRoles={["REVENDEUR", "TRAITEUR", "COURRIER"]}
                linkedContact={linkedContact}
                onUnlink={() => setLinkedContact(null)}
                onSelect={(c) => {
                  setDebtorForm({
                    debtor_name: c.organization || c.name || "",
                    debtor_contact_name: c.organization ? c.name || "" : "",
                    debtor_address: c.address || "",
                    debtor_postal_code: c.postal_code || "",
                    debtor_city: c.city || "",
                    debtor_country: (c.country || "CH") === "Suisse" ? "CH" : (c.country || "CH"),
                  });
                  setLinkedContact(c);
                  toast.success("Adresse importée depuis le carnet");
                }}
              />
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1.5 block">Nom / Société *</Label>
              <Input
                value={debtorForm.debtor_name || ""}
                onChange={(e) => setDebtorForm({ ...debtorForm, debtor_name: e.target.value })}
                data-testid="debtor-name-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Contact (optionnel)</Label>
              <Input
                value={debtorForm.debtor_contact_name || ""}
                onChange={(e) => setDebtorForm({ ...debtorForm, debtor_contact_name: e.target.value })}
                data-testid="debtor-contact-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Rue et n° *</Label>
              <Input
                value={debtorForm.debtor_address || ""}
                onChange={(e) => setDebtorForm({ ...debtorForm, debtor_address: e.target.value })}
                placeholder="Ex : Chemin des Bugs 5"
                data-testid="debtor-address-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="mb-1.5 block">NPA *</Label>
                <Input
                  value={debtorForm.debtor_postal_code || ""}
                  onChange={(e) => setDebtorForm({ ...debtorForm, debtor_postal_code: e.target.value })}
                  placeholder="1000"
                  data-testid="debtor-postal-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="mb-1.5 block">Ville *</Label>
                <Input
                  value={debtorForm.debtor_city || ""}
                  onChange={(e) => setDebtorForm({ ...debtorForm, debtor_city: e.target.value })}
                  placeholder="Lausanne"
                  data-testid="debtor-city-input"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Pays</Label>
              <Input
                value={debtorForm.debtor_country || ""}
                onChange={(e) => setDebtorForm({ ...debtorForm, debtor_country: e.target.value })}
                placeholder="CH"
                data-testid="debtor-country-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDebtorEditOpen(false)}>Annuler</Button>
            <Button
              onClick={async () => {
                setDebtorSaving(true);
                try {
                  await api.put(`/invoices/${id}`, debtorForm);
                  toast.success("Adresse débiteur mise à jour");
                  setDebtorEditOpen(false);
                  reload();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur mise à jour");
                } finally {
                  setDebtorSaving(false);
                }
              }}
              disabled={debtorSaving}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="debtor-save-btn"
            >
              {debtorSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
